import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import referralApi from '../../services/referralApi';

// /Referral/Me'yi tek noktadan çeker; layout ve referral sayfası paylaşır.
// `force` verilmedikçe AYNI şirket için zaten yüklenmiş/yükleniyorsa çağrı
// atlanır (dedup). Arg: { companyId, force } — companyId, şirket değiştiriciden
// seçili şirkettir (state.companyReducer.selected.id); yoksa API kullanıcının
// ilk şirketini döner (eski davranış).
const idOf = (arg) => arg?.companyId ?? null;

export const fetchReferralMe = createAsyncThunk(
  'referral/fetchMe',
  async (arg, { rejectWithValue }) => {
    try {
      const res = await referralApi.me(arg?.companyId || undefined);
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to load referral data',
      );
    }
  },
  {
    condition: (arg, { getState }) => {
      const { referralReducer } = getState();
      if (arg?.force) return true;
      // Another company than the one on screen or in flight: always fetch.
      if (idOf(arg) !== referralReducer.companyId) return true;
      return !referralReducer.loading && !referralReducer.loaded;
    },
  },
);

// The programme is available to a company when it is switched on AND pays
// something (owner, 2026-10-22: the Manager app hides a zero-reward
// programme, so the web does too). The sidebar entry and the page read this
// one rule; a missing or non-numeric reward counts as nothing.
export const isReferralAvailable = (data) =>
  data?.enabled === true && Number(data?.rewardamount) > 0;

const initialState = {
  data: null,
  loading: false,
  loaded: false,
  error: null,
  // The company the data (or the request in flight) belongs to.
  companyId: null,
};

export const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralMe.pending, (state, { meta }) => {
        // A different company than the one held: its link and numbers must not
        // stay on screen under the new company (a failed fetch would leave
        // them there). Clearing shows the page's existing loading skeleton.
        if (idOf(meta.arg) !== state.companyId) {
          state.data = null;
          state.loaded = false;
        }
        state.companyId = idOf(meta.arg);
        state.loading = true;
        state.error = null;
      })
      // A late answer for a company the user already switched away from is
      // dropped: the slice holds the id of the latest request, and only that
      // request's answer (same idiom as companySlice's fetchConnectStatus,
      // meta.arg against the current id) writes. Two requests for the same
      // company (a forced refresh) hold the same data, so either may land.
      .addCase(fetchReferralMe.fulfilled, (state, { payload, meta }) => {
        if (idOf(meta.arg) !== state.companyId) return;
        state.loading = false;
        state.loaded = true;
        state.data = payload;
      })
      .addCase(fetchReferralMe.rejected, (state, { payload, meta }) => {
        if (idOf(meta.arg) !== state.companyId) return;
        state.loading = false;
        state.error = payload || 'Failed to load referral data';
      });
  },
});

export default referralSlice.reducer;
